import React, { useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { Button } from "../ui";
import StarsIcon from "../../styles/StarsIcon";
import { PLACEHOLDER } from "./threadFormat";
import {
  ACCEPT_ATTR,
  validateFiles,
} from "../../shared/_helpers/attachmentPolicy";

// SP2 (W2): placeholder, helperText, sendLabel and allowAttach are for the
// advisor inbox; their defaults are SP1's client composer exactly.
export default function Composer({
  onSend,
  disabled,
  placeholder = PLACEHOLDER,
  helperText = null,
  sendLabel = "Send",
  allowAttach = true,
}) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const pick = (e) => {
    const { ok, rejected } = validateFiles(Array.from(e.target.files || []));
    rejected.forEach((r) =>
      enqueueSnackbar(`${r.file.name}: ${r.reason}`, { variant: "error" }),
    );
    setFiles((prev) => [...prev, ...ok]);
    e.target.value = "";
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!text.trim() && files.length === 0) return;
    const draft = { text: text.trim(), files };
    setText("");
    setFiles([]);
    await onSend(draft);
  };

  return (
    <form data-slot="thread-composer" className="tw-pt-2" onSubmit={submit}>
      {files.length > 0 && (
        <ul
          data-slot="thread-composer-files"
          className="tw-m-0 tw-mb-2 tw-list-none tw-p-0 tw-text-xs tw-text-text"
        >
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              {f.name}{" "}
              <Button
                variant="text"
                size="sm"
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
                aria-label={`Remove ${f.name}`}
              >
                ×
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div
        data-slot="thread-composer-row"
        className="tw-flex tw-items-end tw-gap-2 tw-rounded-composer tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-2"
      >
        {allowAttach && (
          <>
            <Button
              variant="icon"
              iconOnly
              className="tw-border-0"
              onClick={() => inputRef.current?.click()}
              aria-label="Attach files"
              disabled={disabled}
            >
              <StarsIcon
                name="paperclip"
                className="tw-h-5 tw-w-5 tw-shrink-0"
              />
            </Button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              onChange={pick}
              hidden
            />
          </>
        )}
        <textarea
          data-slot="thread-composer-input"
          className="tw-min-h-11 tw-flex-1 tw-resize-none tw-border-0 tw-bg-transparent tw-px-2 tw-py-3 tw-font-sans tw-text-md tw-text-text placeholder:tw-text-text-placeholder focus:tw-outline-none"
          rows={2}
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) submit(e);
          }}
          disabled={disabled}
          maxLength={8000}
        />
        <Button
          type="submit"
          iconOnly
          aria-label={sendLabel}
          title={sendLabel}
          disabled={disabled}
        >
          <StarsIcon name="send" className="tw-h-5 tw-w-5 tw-shrink-0" />
        </Button>
      </div>
      {helperText && (
        <p
          data-slot="thread-composer-helper"
          className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-text-muted"
        >
          {helperText}
        </p>
      )}
    </form>
  );
}
